// Personal Signal Journal — reflection rules.
//
// Governed by docs/governance/signal-journal-packet.md. Everything here works
// only on the entries passed in (the signed-in Warrior's own records) and only
// reflects what was recorded alongside past crises. Crisis entries alone cannot
// show what causes or predicts a crisis, so nothing here scores, predicts,
// ranks risk, recommends treatment, or brings in community data.

export const RULE_VERSION = "sj-rules-1";
export const MIN_ENTRIES = 2; // below this the journal stays silent
export const MIN_REPEATS = 2; // a pattern must appear in at least this many entries
export const MAX_REFLECTIONS = 3;

// Fields a reflection may read. Free-text notes are deliberately excluded so
// a Warrior's own words are never re-presented as a system observation.
export const SOURCE_FIELDS = ["triggered", "treatment", "working", "submitted", "pain"];

export const CRISIS_ONLY_NOTE =
  "These are things you recorded alongside past crises. Crisis entries on their own can't show what causes or predicts a crisis — you know your body best.";

// Governed vocabulary (Codex correction 2). Only these tracker options can be
// reflected back, under these labels. Anything else (free text, Tally option
// IDs, typos, future options not yet reviewed) is dropped, never echoed.
export const TRIGGER_LABELS = Object.freeze({
  "Cold weather exposure": "Thermal shock",
  "High stress situation": "High stress",
  "Lack of sleep": "Lack of sleep",
  "Dehydration (missed water intake)": "Dehydration",
  "Menstrual cycle": "Menstrual cycle",
  "Illness (cold, flu, infection)": "Illness/infection",
  "Physical overexertion": "Overexertion",
  "Changed altitude/air pressure": "Altitude/air pressure",
  "Skipped medication": "Skipped medication",
  "Sex": "Sexual activity", // label chosen by Jason, 2026-10-01
  "Temperature or weather change": "Weather change",
});

export const ACTION_LABELS = Object.freeze({
  "Prescription pain medication": "Prescription pain medication",
  "Over-the-counter pain relief": "Over-the-counter pain relief",
  "Heating pad": "Heating pad",
  "Hydration": "Hydration",
  "Rest": "Rest",
  "Breathing exercises": "Breathing exercises",
  "Oxygen therapy": "Oxygen therapy",
  "Fluids": "Fluids",
});

const lookup = (table, raw) =>
  typeof raw === "string" && Object.prototype.hasOwnProperty.call(table, raw.trim())
    ? table[raw.trim()]
    : null;

// Label for a governed trigger option, or null if it is not on the list.
export const triggerLabel = (raw) => lookup(TRIGGER_LABELS, raw);
// Label for a governed treatment option, or null if it is not on the list.
export const actionLabel = (raw) => lookup(ACTION_LABELS, raw);

export const FEEDBACK_OPTIONS = [
  { id: "accurate", label: "Accurate" },
  { id: "not_me", label: "Not me" },
  { id: "not_enough_data", label: "Not enough data" },
  { id: "harmful", label: "Harmful" },
];

function factorsOf(entry) {
  const seen = new Set();
  (entry.triggered || []).forEach((t) => {
    const l = triggerLabel(t);
    if (l) seen.add(l);
  });
  return [...seen].sort();
}

// Stable key for a set of factors, used for mute / feedback / plans.
export function reflectionKey(factors) {
  return [...factors].sort().join(" + ");
}

function evidenceOf(entry) {
  return { id: entry.id, date: entry.submitted || "", pain: entry.pain || null };
}

// What the Warrior recorded using in the supporting entries, with how many
// times each was recorded. This is their own record, not a suggestion.
function recordedActions(entries) {
  const counts = {};
  entries.forEach((e) => {
    const seen = new Set();
    (e.treatment || []).forEach((t) => { const l = actionLabel(t); if (l) seen.add(l); });
    seen.forEach((l) => { counts[l] = (counts[l] || 0) + 1; });
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, count]) => ({ name, count }));
}

export function describeReflection(r) {
  const what = r.factors.length === 1
    ? r.factors[0]
    : `${r.factors.slice(0, -1).join(", ")} and ${r.factors[r.factors.length - 1]} together`;
  const howMany = r.count === r.total ? `all ${r.total}` : `${r.count} of your ${r.total}`;
  return `In ${howMany} ${r.count === r.total ? "of your " : ""}recorded crises, you noted ${what}.`;
}

/**
 * Build reflections from a Warrior's own entries.
 *
 * @param {Array} entries  the Warrior's own crisis entries (mapCrisisRow shape)
 * @param {Object} opts
 *   excludedFactors    factor labels the Warrior chose not to use
 *   mutedKeys          reflection keys the Warrior muted
 * @returns {{status: "silent"|"no_repeats"|"ok", total: number, reflections: Array}}
 */
export function buildReflections(entries, opts = {}) {
  const excluded = new Set(opts.excludedFactors || []);
  const muted = new Set(opts.mutedKeys || []);
  const list = (entries || []).filter(Boolean);
  const total = list.length;

  if (total < MIN_ENTRIES) return { status: "silent", total, reflections: [] };

  // Count single factors and factor pairs, remembering which entries support each.
  const groups = {};
  list.forEach((e) => {
    const factors = factorsOf(e).filter((f) => !excluded.has(f));
    const add = (fs) => {
      const key = reflectionKey(fs);
      if (!groups[key]) groups[key] = { factors: fs, entries: [] };
      groups[key].entries.push(e);
    };
    factors.forEach((f) => add([f]));
    for (let i = 0; i < factors.length; i++)
      for (let j = i + 1; j < factors.length; j++) add([factors[i], factors[j]]);
  });

  const repeated = Object.entries(groups).filter(([, g]) => g.entries.length >= MIN_REPEATS);
  if (!repeated.length) return { status: "no_repeats", total, reflections: [] };

  // Prefer the most-recorded; on ties prefer pairs (more specific), then name.
  repeated.sort(([ka, a], [kb, b]) =>
    b.entries.length - a.entries.length ||
    b.factors.length - a.factors.length ||
    ka.localeCompare(kb));

  // A single factor adds nothing when a chosen pair already contains it with the same count.
  const chosen = [];
  for (const [key, g] of repeated) {
    if (chosen.length >= MAX_REFLECTIONS) break;
    const redundant = g.factors.length === 1 && chosen.some((c) =>
      c.factors.includes(g.factors[0]) && c.count === g.entries.length);
    if (redundant) continue;
    chosen.push({
      key,
      factors: g.factors,
      count: g.entries.length,
      total,
      evidence: g.entries
        .map(evidenceOf)
        .sort((a, b) => a.date.localeCompare(b.date)),
      recordedActions: recordedActions(g.entries),
      muted: muted.has(key),
      ruleVersion: RULE_VERSION,
      sourceFields: SOURCE_FIELDS,
    });
  }
  chosen.forEach((r) => { r.text = describeReflection(r); });

  return { status: "ok", total, reflections: chosen };
}

// All factor labels present in the Warrior's own entries (for the "which
// factors matter to me" control).
export function availableFactors(entries) {
  const all = new Set();
  (entries || []).forEach((e) => factorsOf(e).forEach((f) => all.add(f)));
  return [...all].sort();
}
